<template>
  <div class="sign-flow">
    <!-- Header -->
    <div class="sign-flow__header">
      <button class="sign-flow__back" @click="handleBack">
        <i class="el-icon-arrow-left"></i>
      </button>
      <h2 class="sign-flow__title">Firmar documento</h2>
    </div>

    <!-- Steps indicator -->
    <div class="sign-flow__steps">
      <div
        v-for="(s, idx) in steps"
        :key="idx"
        class="sign-flow__step"
        :class="{
          'sign-flow__step--active': step === idx + 1,
          'sign-flow__step--done': step > idx + 1
        }"
      >
        <div class="sign-flow__step-circle">
          <i v-if="step > idx + 1" class="el-icon-check"></i>
          <span v-else>{{ idx + 1 }}</span>
        </div>
        <span class="sign-flow__step-label">{{ s }}</span>
      </div>
    </div>

    <!-- Step Content -->
    <div class="sign-flow__body">
      <!-- ═══ STEP 1: Upload PDF + Certificate ═══ -->
      <div v-if="step === 1" class="sign-flow__section">
        <div class="sf-card">
          <h3 class="sf-card__title">
            <i class="el-icon-document"></i> Documento y certificado
          </h3>
          <p class="sf-card__desc">
            Sube el PDF que deseas firmar y selecciona tu certificado
            electrónico.
          </p>

          <!-- Upload area -->
          <div
            class="sf-upload"
            :class="{ 'sf-upload--active': dragOver }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="handleDrop"
            @click="$refs.fileInput.click()"
          >
            <input
              ref="fileInput"
              type="file"
              accept="application/pdf"
              style="display:none"
              @change="handleFileSelect"
            />
            <div v-if="!pdfFile" class="sf-upload__placeholder">
              <i class="el-icon-upload2"></i>
              <span>Arrastra tu PDF aquí o haz clic para seleccionar</span>
              <small>Máximo 20 MB</small>
            </div>
            <div v-else class="sf-upload__file">
              <i class="el-icon-document"></i>
              <div class="sf-upload__file-info">
                <span class="sf-upload__file-name">{{ pdfFile.name }}</span>
                <span class="sf-upload__file-size">{{
                  formatSize(pdfFile.size)
                }}</span>
              </div>
              <button class="sf-upload__remove" @click.stop="removeFile">
                <i class="el-icon-close"></i>
              </button>
            </div>
          </div>

          <div class="sf-row" style="margin-top: 20px">
            <div class="sf-field sf-field--flex">
              <label>Nombre del documento</label>
              <el-input v-model="docName" placeholder="Nombre descriptivo" />
            </div>
            <div class="sf-field" style="width: 180px; flex-shrink: 0">
              <label>Tipo</label>
              <el-select v-model="docType" style="width:100%">
                <el-option value="contrato" label="Contrato" />
                <el-option value="acuerdo" label="Acuerdo" />
                <el-option value="acta" label="Acta" />
                <el-option value="autorizacion" label="Autorización" />
                <el-option value="otro" label="Otro" />
              </el-select>
            </div>
          </div>

          <!-- Divider -->
          <div class="sf-section-divider">
            <i class="el-icon-key"></i>
            <span>Certificado de firma electrónica</span>
          </div>

          <!-- Saved certificates -->
          <div v-if="certificates.length" class="sf-certs">
            <div
              v-for="cert in certificates"
              :key="cert.id"
              class="sf-cert"
              :class="{ 'sf-cert--selected': selectedCertId === cert.id }"
              @click="selectCert(cert)"
            >
              <div class="sf-cert__icon">
                <i class="el-icon-key"></i>
              </div>
              <div class="sf-cert__info">
                <span class="sf-cert__name">{{
                  cert.certificate_name || cert.name || "Certificado"
                }}</span>
                <span class="sf-cert__meta">
                  <template v-if="cert.source === 'sri'"
                    >Importado de Facturación Electrónica</template
                  >
                  <template v-else
                    >Subido el {{ cert.created_at | date }}</template
                  >
                  <template v-if="cert.certificate_expires_at">
                    · Vence: {{ cert.certificate_expires_at | date }}</template
                  >
                </span>
              </div>
              <i
                v-if="selectedCertId === cert.id"
                class="el-icon-circle-check sf-cert__check"
              ></i>
            </div>
          </div>

          <!-- Collapse: upload new -->
          <div
            class="sf-upload-toggle"
            @click="showUploadCert = !showUploadCert"
          >
            <i
              :class="
                showUploadCert ? 'el-icon-arrow-up' : 'el-icon-arrow-down'
              "
            ></i>
            <span>{{
              certificates.length
                ? "Usar otro certificado"
                : "Subir certificado .p12"
            }}</span>
          </div>

          <div
            v-if="showUploadCert || !certificates.length"
            class="sf-upload-cert-section"
          >
            <div class="sf-row">
              <div class="sf-field sf-field--flex">
                <label>Archivo .p12</label>
                <div class="sf-file-input" @click="$refs.p12Input.click()">
                  <input
                    ref="p12Input"
                    type="file"
                    accept=".p12,.pfx"
                    style="display:none"
                    @change="handleP12Select"
                  />
                  <i class="el-icon-key"></i>
                  <span>{{
                    p12File ? p12File.name : "Seleccionar archivo .p12"
                  }}</span>
                </div>
              </div>
              <div class="sf-field" style="width: 220px; flex-shrink: 0">
                <label>Clave del certificado</label>
                <el-input
                  v-model="certPassword"
                  type="password"
                  placeholder="Clave de tu .p12"
                  show-password
                />
              </div>
            </div>
            <el-checkbox v-model="saveCertificate" style="margin-top: 4px">
              Guardar certificado para futuras firmas
            </el-checkbox>
          </div>
        </div>
      </div>

      <!-- ═══ STEP 2: Preview + Place signature zone ═══ -->
      <div
        v-if="step === 2"
        class="sign-flow__section sign-flow__section--split"
      >
        <div class="sf-pdf-panel">
          <PdfViewer
            ref="pdfViewer"
            :pdfData="pdfArrayBuffer"
            :signatureZone="sigZone"
            :editable="true"
            @zone-update="onZoneUpdate"
          />
        </div>
        <div class="sf-side-panel">
          <div class="sf-card">
            <h3 class="sf-card__title">Ubicación de tu firma</h3>
            <p class="sf-card__desc">
              Arrastra el recuadro verde sobre el PDF para indicar dónde se
              colocará tu firma electrónica.
            </p>
            <div class="sf-zone-hint">
              <i class="el-icon-info"></i>
              <span
                >Firma en la <strong>página {{ sigZone.page }}</strong></span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ STEP 3: Signers (optional) + Review + Legal ═══ -->
      <div
        v-if="step === 3"
        class="sign-flow__section sign-flow__section--split"
      >
        <div class="sf-pdf-panel">
          <PdfViewer
            :pdfData="pdfArrayBuffer"
            :displayZones="[previewSignatureZone]"
            :editable="false"
          />
        </div>
        <div class="sf-side-panel">
          <!-- Signers card — hidden until v2 -->
          <div v-if="false" class="sf-card" style="margin-bottom: 16px">
            <h3 class="sf-card__title">
              Firmantes adicionales
              <span class="sf-optional">(opcional)</span>
            </h3>

            <!-- Signer list -->
            <div v-if="signers.length" class="sf-signers">
              <div
                v-for="(signer, idx) in signers"
                :key="idx"
                class="sf-signer"
              >
                <div class="sf-signer__order">{{ idx + 1 }}</div>
                <div class="sf-signer__info">
                  <strong>{{ signer.name }}</strong>
                  <span>{{ signer.email }}</span>
                </div>
                <div class="sf-signer__method">
                  <el-tag
                    size="mini"
                    :type="signer.sign_method === 'certificate' ? '' : 'info'"
                  >
                    {{
                      signer.sign_method === "certificate" ? ".p12" : "Dibujo"
                    }}
                  </el-tag>
                </div>
                <button class="sf-signer__remove" @click="removeSigner(idx)">
                  <i class="el-icon-close"></i>
                </button>
              </div>
            </div>

            <!-- Add signer form -->
            <div class="sf-add-signer">
              <div class="sf-add-signer__row">
                <el-input
                  v-model="newSigner.name"
                  placeholder="Nombre"
                  size="small"
                />
                <el-input
                  v-model="newSigner.email"
                  placeholder="Email"
                  size="small"
                />
                <el-select
                  v-model="newSigner.sign_method"
                  size="small"
                  style="width:140px"
                >
                  <el-option value="drawn" label="Firma dibujo" />
                  <el-option value="certificate" label="Firma .p12" />
                </el-select>
                <button
                  class="md__btn-primary md__btn--sm"
                  @click="addSigner"
                  :disabled="!canAddSigner"
                >
                  <i class="el-icon-plus"></i>
                </button>
              </div>
            </div>

            <div
              v-if="signers.length"
              class="sf-field"
              style="margin-top: 12px"
            >
              <label>Mensaje para los firmantes</label>
              <el-input
                v-model="sendMessage"
                type="textarea"
                :rows="3"
                placeholder="Escribe el mensaje que acompañará la solicitud de firma..."
              />
            </div>
          </div>

          <!-- Review + Legal -->
          <div class="sf-card">
            <h3 class="sf-card__title">Resumen</h3>
            <div class="sf-summary">
              <div class="sf-summary__row">
                <span>Documento:</span>
                <strong>{{ docName }}</strong>
              </div>
              <div class="sf-summary__row">
                <span>Tipo:</span>
                <strong>{{ docType }}</strong>
              </div>
              <div class="sf-summary__row">
                <span>Certificado:</span>
                <strong>{{ certDisplayName }}</strong>
              </div>
              <div v-if="signers.length" class="sf-summary__row">
                <span>Firmantes:</span>
                <strong>{{ signers.length }}</strong>
              </div>
            </div>

            <div class="sf-legal">
              <el-checkbox v-model="legalAccepted">
                Declaro que he leído el documento y acepto firmarlo
                electrónicamente conforme a la Ley 67 de Comercio Electrónico
                del Ecuador.
              </el-checkbox>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ STEP 4: Processing / Result ═══ -->
      <div v-if="step === 4" class="sign-flow__section">
        <div
          class="sf-card sf-result"
          :class="{ 'sf-result--error': signError }"
        >
          <!-- Processing -->
          <div v-if="signing" class="sf-result__loading">
            <i class="el-icon-loading"></i>
            <h3>Firmando documento...</h3>
            <p>
              Estamos aplicando tu firma electrónica. Esto puede tomar unos
              segundos.
            </p>
          </div>

          <!-- Success -->
          <div v-else-if="signSuccess" class="sf-result__success">
            <div class="sf-result__icon sf-result__icon--success">
              <i class="el-icon-circle-check"></i>
            </div>
            <h3>Documento firmado exitosamente</h3>
            <p v-if="signers.length">
              El documento ha sido enviado a los firmantes adicionales.
            </p>
            <p v-else>Tu documento ha sido firmado electrónicamente.</p>

            <div
              class="sf-result__cert"
              v-if="signResult && signResult.certificate"
            >
              <div class="sf-result__cert-row">
                <span>Firmante:</span>
                <strong>{{ signResult.certificate.name }}</strong>
              </div>
              <div class="sf-result__cert-row">
                <span>Serial:</span>
                <strong>{{ signResult.certificate.serial }}</strong>
              </div>
            </div>

            <div class="sf-result__actions">
              <button class="md__btn-primary" @click="downloadSigned">
                <i class="el-icon-download"></i> Descargar PDF firmado
              </button>
              <button class="md__btn-third-secondary" @click="goToDetail">
                Ver detalle
              </button>
              <button class="md__btn-third-secondary" @click="goToList">
                Ir a documentos
              </button>
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="signError" class="sf-result__error-msg">
            <div class="sf-result__icon sf-result__icon--error">
              <i class="el-icon-circle-close"></i>
            </div>
            <h3>Error al firmar</h3>
            <p>{{ signError }}</p>
            <button class="md__btn-primary" @click="step = 3">
              Reintentar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom action bar -->
    <div class="sign-flow__footer" v-if="step < 4">
      <button
        v-if="step > 1"
        class="md__btn-third-secondary sign-flow__footer-back"
        @click="prevStep"
      >
        Anterior
      </button>
      <button class="md__btn-primary" :disabled="!canAdvance" @click="nextStep">
        {{ nextLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import documentoService from "../services/documentoService";
import PdfViewer from "../components/PdfViewer.vue";

export default {
  name: "DocumentSignFlow",
  components: { PdfViewer },
  filters: {
    date(val) {
      if (!val) return "";
      return new Date(val).toLocaleDateString("es-EC");
    }
  },
  data() {
    return {
      step: 1,
      steps: ["Documento", "Ubicar firma", "Revisar y firmar", "Resultado"],
      // Quote link
      quoteId: null,
      // Step 1
      pdfFile: null,
      pdfArrayBuffer: null,
      docName: "",
      docType: "contrato",
      dragOver: false,
      // Step 1 — certificate
      certificates: [],
      selectedCertId: null,
      p12File: null,
      certPassword: "",
      saveCertificate: false,
      showUploadCert: false,
      // Step 2
      sigZone: { page: 1, x: 50, y: 70, w: 25, h: 8, label: "Tu firma aquí" },
      // Step 3
      signers: [],
      newSigner: { name: "", email: "", sign_method: "drawn" },
      sendMessage:
        "Hola, te envío este documento para tu revisión y firma electrónica. " +
        "Por favor revísalo y fírmalo desde el enlace que encontrarás en este correo. " +
        "Si tienes alguna duda, no dudes en contactarme.",
      legalAccepted: false,
      // Step 4
      signing: false,
      signSuccess: false,
      signError: null,
      signResult: null,
      createdDocId: null,
      // Draft mode: pre-existing document loaded from ?documentId
      isDraftMode: false
    };
  },
  computed: {
    canAdvance() {
      switch (this.step) {
        case 1: {
          const hasFile = this.isDraftMode
            ? !!this.pdfArrayBuffer
            : !!this.pdfFile && this.docName.trim().length > 0;
          const hasCert =
            this.selectedCertId ||
            (this.p12File && this.certPassword.length > 0);
          return hasFile && hasCert;
        }
        case 2:
          return true;
        case 3:
          return this.legalAccepted;
        default:
          return false;
      }
    },
    nextLabel() {
      if (this.step === 3) return "Firmar documento";
      return "Siguiente";
    },
    canAddSigner() {
      return (
        this.newSigner.name.trim().length > 0 &&
        this.newSigner.email.trim().length > 0 &&
        this.newSigner.email.includes("@")
      );
    },
    certDisplayName() {
      if (this.selectedCertId) {
        const cert = this.certificates.find(c => c.id === this.selectedCertId);
        return cert
          ? cert.certificate_name || cert.name
          : "Certificado guardado";
      }
      return this.p12File ? this.p12File.name : "No seleccionado";
    },
    previewSignatureZone() {
      return {
        ...this.sigZone,
        label: "✓ Firmado por: " + this.certDisplayName,
        signed: true
      };
    }
  },
  async created() {
    await this.loadCertificates();
    // Pre-fill from quote context if coming from QuoteView
    if (this.$route.query.quoteId) {
      this.quoteId = this.$route.query.quoteId;
    }
    if (this.$route.query.quoteName) {
      this.docName = this.$route.query.quoteName;
    }
    // Load existing draft document — skip upload step
    if (this.$route.query.documentId) {
      await this.loadDraftDocument(this.$route.query.documentId);
    }
  },
  methods: {
    handleBack() {
      if (this.step > 1 && this.step < 4) {
        this.prevStep();
      } else {
        this.$router.push({ name: "documentos" });
      }
    },

    prevStep() {
      if (this.step > 1) this.step--;
    },

    async nextStep() {
      if (this.step === 3) {
        await this.executeSign();
        return;
      }
      if (this.step < 4) this.step++;

      // Load PDF buffer when entering step 2
      if (this.step === 2 && !this.pdfArrayBuffer) {
        await this.loadPdfBuffer();
      }
    },

    // ─── Step 1: File handling ────────────────────────────────────
    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) this.setFile(file);
    },

    handleDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files[0];
      if (file && file.type === "application/pdf") {
        this.setFile(file);
      }
    },

    setFile(file) {
      if (file.size > 20 * 1024 * 1024) {
        this.$notifications.error("El archivo no puede superar 20 MB.");
        return;
      }
      this.pdfFile = file;
      if (!this.docName) {
        this.docName = file.name.replace(/\.pdf$/i, "");
      }
      this.pdfArrayBuffer = null; // Reset
    },

    removeFile() {
      this.pdfFile = null;
      this.pdfArrayBuffer = null;
      this.docName = "";
    },

    loadPdfBuffer() {
      if (!this.pdfFile) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => {
          this.pdfArrayBuffer = new Uint8Array(e.target.result);
          resolve();
        };
        reader.onerror = () => reject(new Error("Error al leer el archivo"));
        reader.readAsArrayBuffer(this.pdfFile);
      });
    },

    formatSize(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },

    // ─── Step 2: Zone ─────────────────────────────────────────────
    onZoneUpdate(zone) {
      this.sigZone = { ...this.sigZone, ...zone };
    },

    // ─── Step 1: Certificates ─────────────────────────────────────
    async loadCertificates() {
      try {
        const resp = await documentoService.getCertificates();
        const data = resp && resp.data ? resp.data : null;
        if (data && data.success) {
          this.certificates = data.data || [];
          const defaultCert = this.certificates.find(c => c.is_default);
          if (defaultCert) {
            this.selectedCertId = defaultCert.id;
          } else if (this.certificates.length === 1) {
            this.selectedCertId = this.certificates[0].id;
          }
        }
        if (!this.certificates.length) {
          await this.tryImportSriCertificate();
        }
      } catch (e) {
        this.certificates = [];
      }
    },

    async tryImportSriCertificate() {
      try {
        const resp = await documentoService.importSriCertificate();
        const data = resp && resp.data ? resp.data : null;
        if (data && data.success && data.data) {
          this.certificates = [data.data];
          this.selectedCertId = data.data.id;
        }
      } catch (e) {
        // No SRI certificate available
      }
    },

    selectCert(cert) {
      this.selectedCertId = cert.id;
      this.p12File = null;
    },

    handleP12Select(e) {
      const file = e.target.files[0];
      if (file) {
        this.p12File = file;
        this.selectedCertId = null;
      }
    },

    // ─── Step 3: Signers ──────────────────────────────────────────
    addSigner() {
      if (!this.canAddSigner) return;
      this.signers.push({ ...this.newSigner });
      this.newSigner = { name: "", email: "", sign_method: "drawn" };
    },

    removeSigner(idx) {
      this.signers.splice(idx, 1);
    },

    // ─── Load pre-existing draft document ────────────────────────
    async loadDraftDocument(id) {
      try {
        // Load metadata
        const metaRes = await documentoService.getDocument(id);
        const doc = metaRes?.data?.data;
        if (doc) {
          this.docName = doc.name || "";
          this.docType = doc.type || "contrato";
          this.createdDocId = doc.id;
          this.isDraftMode = true;
        }
        // Load PDF buffer from server
        const pdfRes = await documentoService.previewPdf(id);
        if (pdfRes?.data) {
          this.pdfArrayBuffer = new Uint8Array(pdfRes.data);
        }
        // Jump directly to step 2
        this.step = 2;
      } catch (e) {
        this.$notifications.error({
          message: "No se pudo cargar el documento."
        });
      }
    },

    // ─── Step 3→4: Execute signing ───────────────────────────────
    async executeSign() {
      this.step = 4;
      this.signing = true;
      this.signError = null;
      this.signSuccess = false;

      try {
        let documentId = this.createdDocId;

        if (!this.isDraftMode) {
          // 1. Upload PDF and create document (new flow)
          const uploadPayload = {
            file: this.pdfFile,
            name: this.docName,
            type: this.docType
          };
          if (this.quoteId) {
            uploadPayload.user_business_quote_id = this.quoteId;
          }
          const uploadRes = await documentoService.uploadDocumentPdf(
            uploadPayload
          );
          if (!uploadRes.data.success) {
            throw new Error(
              uploadRes.data.message || "Error al subir el documento."
            );
          }
          documentId = uploadRes.data.data.id;
          this.createdDocId = documentId;

          // 2. Add signers (if any)
          for (const signer of this.signers) {
            await documentoService.addSigner(documentId, signer);
          }
        }

        // 3. Sign as owner with certificate
        const signData = new FormData();
        signData.append("zone[page]", this.sigZone.page);
        signData.append("zone[x]", this.sigZone.x);
        signData.append("zone[y]", this.sigZone.y);
        signData.append("zone[w]", this.sigZone.w);
        signData.append("zone[h]", this.sigZone.h);

        if (this.selectedCertId) {
          signData.append("certificate_id", this.selectedCertId);
        } else if (this.p12File) {
          signData.append("p12_file", this.p12File);
          if (this.saveCertificate) {
            signData.append("save_certificate", "1");
          }
        }
        signData.append("password", this.certPassword);

        const signRes = await documentoService.signOwnerWithCertificate(
          documentId,
          signData
        );

        if (!signRes.data.success) {
          throw new Error(
            signRes.data.message || "Error al firmar el documento."
          );
        }

        this.signResult = signRes.data;

        // 4. If signers exist, send for signature
        if (this.signers.length > 0) {
          await documentoService.sendDocument(documentId, {
            message: this.sendMessage
          });
        }

        this.signSuccess = true;
      } catch (err) {
        this.signError =
          err.response?.data?.message ||
          err.message ||
          "Error inesperado al firmar.";
      } finally {
        this.signing = false;
      }
    },

    async downloadSigned() {
      if (!this.createdDocId) return;
      try {
        const response = await documentoService.downloadDocument(
          this.createdDocId
        );
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${this.docName}_firmado.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$notifications.error("Error al descargar el documento.");
      }
    },

    goToDetail() {
      if (this.createdDocId) {
        this.$router.push({
          name: "documentos.detail",
          params: { id: this.createdDocId }
        });
      }
    },

    goToList() {
      this.$router.push({ name: "documentos" });
    }
  }
};
</script>

<style scoped>
.sign-flow {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: calc(100vh - 60px);
  background: #f7f8f9;
}

/* Header */
.sign-flow__header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #e4eaf6;
  gap: 16px;
}

.sign-flow__back {
  width: 36px;
  height: 36px;
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #31355c;
  font-size: 16px;
}

.sign-flow__back:hover {
  border-color: #3fdbbf;
}

.sign-flow__title {
  flex: 1;
  font-size: 18px;
  font-weight: 700;
  color: #1e1c42;
  margin: 0;
}

/* Bottom action bar */
.sign-flow__footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 24px;
  background: #fff;
  border-top: 1px solid #e4eaf6;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}

.sign-flow__footer-back {
  margin-right: auto;
}

/* Steps */
.sign-flow__steps {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 24px 12px;
  gap: 4px;
}

.sign-flow__step {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sign-flow__step::after {
  content: "";
  width: 40px;
  height: 2px;
  background: #e4eaf6;
  margin: 0 6px;
}

.sign-flow__step:last-child::after {
  display: none;
}

.sign-flow__step--done::after {
  background: #3fdbbf;
}

.sign-flow__step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  background: #e4eaf6;
  color: #989aae;
  transition: all 0.2s;
}

.sign-flow__step--active .sign-flow__step-circle {
  background: #3fdbbf;
  color: #fff;
}

.sign-flow__step--done .sign-flow__step-circle {
  background: #3fdbbf;
  color: #fff;
}

.sign-flow__step-label {
  font-size: 13px;
  color: #989aae;
  font-weight: 500;
}

.sign-flow__step--active .sign-flow__step-label {
  color: #1e1c42;
  font-weight: 600;
}

/* Body */
.sign-flow__body {
  flex: 1;
  /* Extra bottom clearance so content never hides behind the fixed footer */
  padding: 20px 24px 88px;
  overflow: auto;
}

.sign-flow__section {
  max-width: 720px;
  margin: 0 auto;
}

.sign-flow__section--split {
  max-width: 100%;
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 20px;
  /* Accounts for the outer topbar + header + steps + the bottom action bar */
  height: calc(100vh - 292px);
}

/* Cards */
.sf-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}

.sf-card__title {
  font-size: 16px;
  font-weight: 700;
  color: #1e1c42;
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sf-card__title i {
  color: #3fdbbf;
  font-size: 18px;
}

.sf-card__desc {
  font-size: 13px;
  color: #989aae;
  margin: 0 0 18px;
}

/* Row layout */
.sf-row {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.sf-field--flex {
  flex: 1;
}

/* Upload */
.sf-upload {
  border: 2px dashed #e4eaf6;
  border-radius: 12px;
  padding: 32px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.sf-upload:hover,
.sf-upload--active {
  border-color: #3fdbbf;
  background: rgba(63, 219, 191, 0.04);
}

.sf-upload__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #989aae;
}

.sf-upload__placeholder i {
  font-size: 36px;
  color: #3fdbbf;
}

.sf-upload__placeholder small {
  font-size: 11px;
  color: #bbb;
}

.sf-upload__file {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
}

.sf-upload__file > i {
  font-size: 28px;
  color: #31355c;
}

.sf-upload__file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.sf-upload__file-name {
  font-weight: 600;
  color: #31355c;
  font-size: 14px;
}

.sf-upload__file-size {
  font-size: 12px;
  color: #989aae;
}

.sf-upload__remove {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #fee;
  color: #e74c3c;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Section divider */
.sf-section-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f2f5;
  font-size: 16px;
  font-weight: 700;
  color: #1e1c42;
}

.sf-section-divider i {
  color: #3fdbbf;
  font-size: 16px;
}

/* Fields */
.sf-field {
  margin-bottom: 14px;
}

.sf-field label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #31355c;
  margin-bottom: 6px;
}

/* PDF panel */
.sf-pdf-panel {
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}

.sf-side-panel {
  overflow-y: auto;
  padding: 10px 2px 2px; /* space for card box-shadow not to be clipped on all sides */
}

/* Zone hint */
.sf-zone-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: rgba(63, 219, 191, 0.08);
  border-radius: 10px;
  font-size: 13px;
  color: #31355c;
}

.sf-zone-hint i {
  color: #3fdbbf;
  font-size: 16px;
  flex-shrink: 0;
}

/* Certificates */
.sf-certs {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}

.sf-cert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 2px solid #e4eaf6;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.sf-cert:hover {
  border-color: #3fdbbf;
}

.sf-cert--selected {
  border-color: #3fdbbf;
  background: rgba(63, 219, 191, 0.06);
}

.sf-cert__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #31355c;
  font-size: 16px;
}

.sf-cert__info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.sf-cert__name {
  font-weight: 600;
  color: #31355c;
  font-size: 13px;
}

.sf-cert__meta {
  font-size: 11px;
  color: #989aae;
}

.sf-cert__check {
  color: #3fdbbf;
  font-size: 20px;
}

.sf-upload-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 14px 0 10px;
  cursor: pointer;
  font-size: 13px;
  color: #3fdbbf;
  font-weight: 600;
  transition: color 0.15s;
}

.sf-upload-toggle:hover {
  color: #30c5aa;
}

.sf-upload-toggle i {
  font-size: 12px;
}

.sf-upload-cert-section {
  padding-top: 8px;
  border-top: 1px solid #f0f2f5;
}

.sf-file-input {
  border: 1.5px solid #e4eaf6;
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #989aae;
  transition: border-color 0.2s;
  font-size: 13px;
}

.sf-file-input:hover {
  border-color: #3fdbbf;
}

/* Signers */
.sf-signers {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.sf-signer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f7f8f9;
  border-radius: 10px;
}

.sf-signer__order {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #31355c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.sf-signer__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sf-signer__info strong {
  font-size: 13px;
  color: #31355c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sf-signer__info span {
  font-size: 11px;
  color: #989aae;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sf-signer__remove {
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #989aae;
  cursor: pointer;
  flex-shrink: 0;
}

.sf-signer__remove:hover {
  color: #e74c3c;
}

.sf-add-signer__row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.sf-optional {
  font-weight: 400;
  font-size: 12px;
  color: #989aae;
}

/* Summary */
.sf-summary {
  margin: 12px 0;
}

.sf-summary__row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;
}

.sf-summary__row span {
  color: #989aae;
}

.sf-summary__row strong {
  color: #1e1c42;
  text-align: right;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sf-legal {
  margin-top: 16px;
  padding: 14px;
  background: #f7f8f9;
  border-radius: 10px;
  overflow: hidden;
}

.sf-legal .el-checkbox {
  display: flex;
  align-items: flex-start;
  white-space: normal;
}

.sf-legal .el-checkbox__input {
  flex-shrink: 0;
  margin-top: 2px;
}

.sf-legal .el-checkbox__label {
  font-size: 12px;
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
  padding-left: 10px;
}

/* Result */
.sf-result {
  text-align: center;
  padding: 60px 40px;
}

.sf-result__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.sf-result__loading i {
  font-size: 48px;
  color: #3fdbbf;
}

.sf-result__loading h3 {
  color: #1e1c42;
  margin: 0;
}

.sf-result__loading p {
  color: #989aae;
  font-size: 14px;
}

.sf-result__success,
.sf-result__error-msg {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.sf-result__icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
}

.sf-result__icon--success {
  background: rgba(63, 219, 191, 0.12);
  color: #3fdbbf;
}

.sf-result__icon--error {
  background: rgba(231, 76, 60, 0.12);
  color: #e74c3c;
}

.sf-result__success h3,
.sf-result__error-msg h3 {
  color: #1e1c42;
  margin: 0;
}

.sf-result__success p,
.sf-result__error-msg p {
  color: #989aae;
  font-size: 14px;
  max-width: 400px;
}

.sf-result__cert {
  background: #f7f8f9;
  border-radius: 10px;
  padding: 16px 24px;
  margin-top: 12px;
  width: 100%;
  max-width: 360px;
}

.sf-result__cert-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
}

.sf-result__cert-row span {
  color: #989aae;
}

.sf-result__cert-row strong {
  color: #1e1c42;
}

.sf-result__actions {
  display: flex;
  gap: 10px;
  margin-top: 24px;
  flex-wrap: wrap;
  justify-content: center;
}

/* Button styles */
.md__btn-primary {
  background: #3fdbbf;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.md__btn-primary:hover {
  background: #2bc4a8;
}

.md__btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.md__btn-primary.md__btn--sm {
  padding: 6px 14px;
  font-size: 12px;
}

.md__btn-third-secondary {
  background: #f0f2f5;
  color: #31355c;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.md__btn-third-secondary:hover {
  background: #e4eaf6;
}

@media (max-width: 768px) {
  /* Header */
  .sign-flow__header {
    padding: 12px 14px;
    gap: 8px;
  }

  .sign-flow__title {
    font-size: 15px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0; /* allows flex item to shrink below content size */
  }

  .sign-flow__footer {
    padding: 12px 14px;
  }

  /* Steps */
  .sign-flow__section--split {
    grid-template-columns: 1fr;
    height: auto;
  }

  /* En mobile: mostrar cards (Resumen + Firmantes) antes que el PDF */
  .sf-side-panel {
    order: -1;
  }

  .sign-flow__steps {
    padding: 16px 12px 10px;
    gap: 2px;
  }

  .sign-flow__step-label {
    display: none;
  }

  .sign-flow__step::after {
    width: 24px;
    margin: 0 4px;
  }

  .sf-add-signer__row {
    flex-wrap: wrap;
  }

  .sf-row {
    flex-direction: column;
  }

  .sf-row .sf-field {
    width: 100% !important;
  }
}
</style>
